import { useEffect, useState } from 'react';
import { ChefHat, Wine } from 'lucide-react';
import { supabase } from '@/src/lib/supabase';
import { formatMoney } from '@/src/lib/format';
import { pct, addDays, type CostOfGoods } from '@/src/lib/invoices';
import { Kpi, RED } from './numbers-ui';

// Food and drinks cost % for the Numbers date range, from invoices, against the same length of time just before.
// Hidden until the venue has invoices.
export default function NumbersCogs({ from, to, label }: { from: string; to: string; label: string }) {
  const [cur, setCur] = useState<CostOfGoods | null>(null);
  const [prev, setPrev] = useState<CostOfGoods | null>(null);
  useEffect(() => {
    let off = false;
    const days = Math.round((new Date(`${to}T12:00:00`).getTime() - new Date(`${from}T12:00:00`).getTime()) / 864e5) + 1;
    void Promise.all([
      supabase.rpc('cost_of_goods', { p_from: from, p_to: to }),
      supabase.rpc('cost_of_goods', { p_from: addDays(from, -days), p_to: addDays(from, -1) }),
    ]).then(([a, b]) => { if (off) return; setCur((a.data ?? null) as CostOfGoods | null); setPrev((b.data ?? null) as CostOfGoods | null); });
    return () => { off = true; };
  }, [from, to]);
  if (!cur || (!cur.invoices && !prev?.invoices)) return null;
  const card = (kind: 'food' | 'drinks') => {
    const spend = kind === 'food' ? cur.food_spend : cur.drinks_spend;
    const sales = kind === 'food' ? cur.food_sales : cur.drinks_sales;
    const p = pct(spend, sales);
    const pp = prev ? pct(kind === 'food' ? prev.food_spend : prev.drinks_spend, kind === 'food' ? prev.food_sales : prev.drinks_sales) : null;
    const target = kind === 'food' ? cur.food_target : cur.drinks_target;
    return (
      <Kpi key={kind} label={kind === 'food' ? 'Food cost' : 'Drinks cost'} icon={kind === 'food' ? <ChefHat size={15} /> : <Wine size={15} />}
        value={p == null ? 'No data' : `${p}%`}
        valueColor={p != null && target != null && p > Number(target) ? RED : undefined}
        sub={`${formatMoney(spend)} invoices${sales ? ` ÷ ${formatMoney(sales)} sales` : ', no sales'}${target != null ? ` · target ${Number(target)}%` : ''}`}
        delta={p != null && pp != null ? { pct: Math.round((p - pp) * 10) / 10, label: 'vs period before', invert: true, unit: ' pts' } : null} />
    );
  };
  return (
    <div className="grid gap-3 grid-cols-2" aria-label={`Cost of goods, ${label}`}>
      {card('food')}{card('drinks')}
    </div>
  );
}
